import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import JourneyWizard from "../components/JourneyWizard";
import { MapPin, Loader2, ArrowRight, Sparkles } from "lucide-react";

export default function PackageDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const [pkg, setPkg] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => toast.error("Retreat not found"));
  }, [slug]);

  const design = () => {
    if (!user) { toast("Please sign in to design your journey"); return login(`/retreats/${slug}`); }
    setWizardOpen(true);
  };

  if (!pkg) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-rr-gold animate-spin" strokeWidth={1} /></div>;

  return (
    <div data-testid="package-detail-page">
      <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden -mt-[74px] grain">
        <img src={pkg.gallery?.[activeImg] || pkg.hero_image} alt={pkg.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-16">
          <div className="flex items-center gap-2 fade-up"><MapPin className="w-4 h-4 text-rr-gold" strokeWidth={1.4} /><span className="overline">{pkg.area} · Sri Lanka</span></div>
          <h1 className="font-heading text-6xl md:text-8xl leading-[0.92] tracking-tight mt-4 max-w-3xl text-rr-ivory fade-up" style={{ animationDelay: "100ms" }}>{pkg.name}</h1>
          <p className="text-rr-ivory/85 text-lg md:text-2xl font-heading italic mt-4 max-w-2xl fade-up" style={{ animationDelay: "200ms" }}>{pkg.tagline}</p>
          <div className="flex flex-wrap gap-2.5 mt-7 fade-up" style={{ animationDelay: "300ms" }}>
            {pkg.highlights?.map((h) => <span key={h} className="text-xs tracking-wide px-4 py-2 rounded-full border border-rr-ivory/25 bg-black/20 text-rr-ivory/90">{h}</span>)}
          </div>
        </div>
        <div className="absolute bottom-6 right-6 z-10 hidden md:flex gap-2">
          {(pkg.gallery || []).slice(0, 4).map((g, i) => (
            <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
              className={`w-16 h-16 rounded overflow-hidden border-2 transition-all ${activeImg === i ? "border-rr-gold" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-[1fr_410px] gap-14">
        <div>
          <div className="hairline mb-6" />
          <span className="overline">The Setting</span>
          <p className="font-heading text-2xl md:text-4xl leading-snug mt-5 text-rr-ivory">{pkg.description}</p>
          <div className="grid grid-cols-2 gap-5 mt-12">
            {(pkg.gallery || []).map((g, i) => (
              <div key={i} className="rounded-xl overflow-hidden h-60 group"><img src={g} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]" /></div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-[98px] self-start">
          <div className="rounded-xl border border-rr-line bg-rr-surface p-6 md:p-7">
            <span className="overline text-rr-muted">Your Sanctuary</span>
            <p className="font-heading text-3xl text-rr-ivory mt-2">{pkg.name}</p>
            <p className="text-sm text-rr-muted mt-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-rr-gold" /> {pkg.area}, Sri Lanka</p>
            <div className="hairline my-6" />
            <p className="text-sm text-rr-ivory/80 leading-relaxed">Choose this setting for any of our Ayurveda programmes — Explorer, Executive Reset or Royal Longevity. Design your journey and see your price in moments.</p>
            {pkg.early_bird_discount_percent > 0 && (
              <p className="text-rr-gold text-sm mt-4 flex items-center gap-1.5" data-testid="package-early-bird-note">
                <Sparkles className="w-3.5 h-3.5" /> Early Bird — save {pkg.early_bird_discount_percent}% when you book now
              </p>
            )}
            <button onClick={design} data-testid="design-here-btn" className="btn-gold w-full py-4 mt-5 flex items-center justify-center gap-2 text-xs uppercase">
              Design Your Journey Here <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <p className="text-[11px] text-rr-muted text-center mt-3 flex items-center justify-center gap-1.5"><Sparkles className="w-3 h-3 text-rr-gold" /> Availability always assured</p>
          </div>
        </div>
      </div>

      {wizardOpen && (
        <JourneyWizard mode="create" preselectedPackage={slug}
          onClose={() => setWizardOpen(false)}
          onSaved={(id) => { setWizardOpen(false); navigate(`/trips/${id}`); }} />
      )}
    </div>
  );
}
